import { supabase } from '@/integrations/supabase/client'

declare global {
  interface Window {
    OneSignal?: any
  }
}

export interface SubscriptionState {
  isSupported: boolean
  isInitialized: boolean
  isSubscribed: boolean
  playerId: string | null
  browserPermission: NotificationPermission
  error: string | null
}

export type PermissionStrategy = 'automaticPrompt' | 'customDialog' | 'delayedPrompt' | 'manualOnly'

class OneSignalService {
  private static instance: OneSignalService
  private isCheckingSubscription = false
  private isSubscribing = false
  private isInitialized: boolean = false
  // Holds the conversation ID that is currently visible on screen.
  // When a push arrives for this conversation, foregroundWillDisplay
  // suppresses it — the user is already reading those messages.
  private activeConversationId: string | null = null
  private subscriptionState: SubscriptionState = {
    isSupported: false,
    isInitialized: false,
    isSubscribed: false,
    playerId: null,
    browserPermission: 'default',
    error: null,
  }
  private listeners: Set<(state: SubscriptionState) => void> = new Set()
  private currentUserId: string | null = null
  private initializationPromise: Promise<void> | null = null

  private constructor() {
    this.checkSupport()
    this.setupBrowserPermissionListener()
    if (typeof window !== 'undefined' && window.OneSignal) {
      setTimeout(() => {
        if (window.OneSignal?.User?.PushSubscription && !this.isInitialized) {
          this.initialize().catch(() => {})
        }
      }, 1000)
    }
  }

  public static getInstance(): OneSignalService {
    if (!OneSignalService.instance) {
      OneSignalService.instance = new OneSignalService()
    }
    return OneSignalService.instance
  }

  private checkSupport(): void {
    const isSupported =
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window

    this.subscriptionState.isSupported = isSupported
    this.subscriptionState.browserPermission =
      typeof Notification !== 'undefined' ? Notification.permission : 'default'

    this.notifyListeners()
  }

  private setupBrowserPermissionListener(): void {
    if (typeof window === 'undefined' || !('Notification' in window)) return

    const checkPermission = () => {
      const currentPermission = Notification.permission

      if (currentPermission === 'denied' && this.isInitialized) {
        window.OneSignal?.User?.PushSubscription?.optOut?.().catch(() => {})
        this.subscriptionState.isSubscribed = false
        this.subscriptionState.playerId = null
      }

      if (currentPermission !== this.subscriptionState.browserPermission) {
        this.subscriptionState.browserPermission = currentPermission
        this.notifyListeners()
        if (this.isInitialized && this.currentUserId) {
          this.checkSubscriptionState()
        }
      }
    }

    document.addEventListener('visibilitychange', checkPermission)
    setInterval(checkPermission, 30000)
  }

  // ── Active conversation tracking ───────────────────────────────────────────
  // Called by ChatWindow on mount/unmount. Keeps a reference to which
  // conversation the user is currently viewing so the foreground handler
  // can suppress redundant push notifications for that conversation.

  public setActiveConversation(conversationId: string | null): void {
    this.activeConversationId = conversationId
  }

  // ── Foreground notification suppression ────────────────────────────────────
  // Registered once during SDK init. Fires before OneSignal displays a
  // notification while the app is open. When the incoming push belongs to
  // the conversation currently on screen, we suppress it — the user is
  // already reading those messages via Realtime.
  //
  // S: single responsibility — this method only decides "show or suppress".
  //    It does not know how presence is tracked or how the push was sent.

  private setupForegroundHandler(): void {
    if (!window.OneSignal?.Notifications?.addEventListener) return

    window.OneSignal.Notifications.addEventListener(
      'foregroundWillDisplay',
      (event: any) => {
        try {
          const data = event?.notification?.additionalData as Record<string, unknown> | undefined
          if (
            data?.type === 'message' &&
            typeof data?.conversation_id === 'string' &&
            data.conversation_id === this.activeConversationId
          ) {
            event.preventDefault()
          }
        } catch {
          // If anything throws, fall through and let the notification display.
          // Fail-open: a redundant push is better than swallowing an error.
        }
      },
    )
  }

  private setupOneSignalListeners(): void {
    if (typeof window === 'undefined' || !window.OneSignal) return

    this.setupForegroundHandler()

    if (window.OneSignal.addListenerForNotificationOpened) {
      window.OneSignal.addListenerForNotificationOpened(() => {
        if (this.isInitialized && this.currentUserId) {
          this.checkSubscriptionState()
        }
      })
    }

    try {
      if (window.OneSignal.User?.PushSubscription?.addEventListener) {
        window.OneSignal.User.PushSubscription.addEventListener('change', () => {
          if (this.isInitialized && this.currentUserId) {
            this.checkSubscriptionState()
          }
        })
      }
    } catch {
      // addEventListener may not be available in all SDK versions
    }

    if (window.OneSignal.User?.PushSubscription) {
      setInterval(() => {
        if (this.isInitialized && this.currentUserId) {
          this.checkSubscriptionState()
        }
      }, 60000)
    }
  }

  public async initialize(appId?: string): Promise<void> {
    if (this.initializationPromise) return this.initializationPromise
    this.initializationPromise = this.doInitialize(appId)
    return this.initializationPromise
  }

  private async doInitialize(appId?: string): Promise<void> {
    if (!this.subscriptionState.isSupported) {
      this.subscriptionState.error = 'Push notifications not supported'
      this.notifyListeners()
      return
    }

    if (this.isInitialized) return

    let retryCount = 0
    const maxRetries = 20

    while (!window.OneSignal && retryCount < maxRetries) {
      await new Promise(resolve => setTimeout(resolve, 500))
      retryCount++
    }

    if (!window.OneSignal) {
      this.subscriptionState.error = 'OneSignal SDK not loaded'
      this.notifyListeners()
      return
    }

    if (appId) {
      try {
        await window.OneSignal.init({
          appId,
          allowLocalhostAsSecureOrigin: true,
          serviceWorkerParam: { scope: '/' },
          serviceWorkerPath: 'OneSignalSDKWorker.js',
          autoResubscribe: true,
          promptOptions: { slidedown: { enabled: false } },
        })
      } catch {
        // init() throws if called more than once — expected, not fatal
      }
    }

    retryCount = 0
    while (!window.OneSignal.User?.PushSubscription && retryCount < maxRetries) {
      await new Promise(resolve => setTimeout(resolve, 500))
      retryCount++
    }

    if (window.OneSignal.User?.PushSubscription) {
      this.isInitialized = true
      this.subscriptionState.isInitialized = true
      this.setupOneSignalListeners()
      if (this.currentUserId && window.OneSignal?.login) {
        window.OneSignal.login(this.currentUserId).catch(() => {})
      }
      await this.checkSubscriptionState()
    } else {
      this.subscriptionState.error = 'OneSignal SDK initialization timeout'
      this.notifyListeners()
    }
  }

  public setUserId(userId: string | null): void {
    if (this.currentUserId !== userId) {
      this.currentUserId = userId
      if (userId && this.isInitialized) {
        if (window.OneSignal?.login) {
          window.OneSignal.login(userId).catch(() => {})
        }
        this.checkSubscriptionState()
      } else if (!userId) {
        if (window.OneSignal?.logout) {
          window.OneSignal.logout().catch(() => {})
        }
        this.subscriptionState.isSubscribed = false
        this.subscriptionState.playerId = null
        this.notifyListeners()
      }
    }
  }

  private async fetchExistingPlayerId(): Promise<string | null> {
    if (!this.currentUserId) return null

    try {
      const { data, error } = await supabase
        .from('onesignal_players')
        .select('player_id')
        .eq('user_id', this.currentUserId)
        .eq('platform', 'web')
        .limit(1)

      if (error) {
        const isExpected = error.code === '42P01' || error.code === 'PGRST116'
        if (!isExpected) console.error('[OneSignalService] Error checking database:', error)
        return null
      }

      return data?.[0]?.player_id ?? null
    } catch (error) {
      console.error('[OneSignalService] Error fetching existing player ID:', error)
      return null
    }
  }

  public async checkSubscriptionState(): Promise<void> {
    if (this.isCheckingSubscription) return
    this.isCheckingSubscription = true

    try {
      if (!this.isInitialized || !window.OneSignal || !this.currentUserId) return

      this.subscriptionState.browserPermission = Notification.permission

      if (Notification.permission === 'denied') {
        window.OneSignal?.User?.PushSubscription?.optOut?.().catch(() => {})
        this.subscriptionState.isSubscribed = false
        this.subscriptionState.playerId = null
        this.notifyListeners()
        return
      }

      const sdkPlayerId: string | null = window.OneSignal.User.PushSubscription.id || null

      if (sdkPlayerId) {
        const { data: dbRow, error } = await supabase
          .from('onesignal_players')
          .select('player_id')
          .eq('user_id', this.currentUserId)
          .eq('player_id', sdkPlayerId)
          .limit(1)

        if (error && error.code !== '42P01' && error.code !== 'PGRST116') {
          console.error('[OneSignalService] Error checking database:', error)
        }

        if (dbRow && dbRow.length > 0) {
          this.subscriptionState.playerId = sdkPlayerId
          this.subscriptionState.isSubscribed = Notification.permission === 'granted'
        } else {
          this.subscriptionState.playerId = null
          this.subscriptionState.isSubscribed = false
        }
      } else {
        const dbPlayerId = await this.fetchExistingPlayerId()
        this.subscriptionState.playerId = dbPlayerId
        this.subscriptionState.isSubscribed = !!dbPlayerId && Notification.permission === 'granted'
      }

      this.subscriptionState.error = null
    } catch (error: any) {
      console.error('[OneSignalService] Error checking subscription:', error)
      this.subscriptionState.error = error?.message || 'Failed to check subscription'
      this.subscriptionState.isSubscribed = false
    } finally {
      this.isCheckingSubscription = false
    }

    this.notifyListeners()
  }

  private async savePlayerIdToDatabase(playerId: string): Promise<void> {
    if (!this.currentUserId || !playerId.trim()) return

    try {
      const { error } = await supabase
        .from('onesignal_players')
        .upsert(
          { user_id: this.currentUserId, player_id: playerId, platform: 'web', updated_at: new Date().toISOString() },
          { onConflict: 'user_id,player_id' }
        )
        .select()

      if (error) {
        console.error('[OneSignalService] Error saving player ID:', error)
        throw error
      }
    } catch (error: any) {
      console.error('[OneSignalService] Failed to save player ID:', error)
    }
  }

  public async subscribe(): Promise<boolean> {
    if (this.isSubscribing) return false
    this.isSubscribing = true

    try {
      if (!this.subscriptionState.isSupported || !this.isInitialized) return false
      if (!this.currentUserId) return false
      if (!window.OneSignal?.Notifications) return false
      if (Notification.permission === 'denied') return false

      if (!navigator.serviceWorker.controller) {
        await new Promise<void>((resolve) => {
          const timeout = setTimeout(resolve, 5000)
          navigator.serviceWorker.addEventListener('controllerchange', () => {
            clearTimeout(timeout)
            resolve()
          }, { once: true })
        })
      }

      const granted = await window.OneSignal.Notifications.requestPermission()
      if (!granted) return false

      await window.OneSignal.User.PushSubscription.optIn()

      let playerId: string | null = null
      for (let i = 0; i < 20; i++) {
        await new Promise(resolve => setTimeout(resolve, 500))
        playerId = window.OneSignal.User?.PushSubscription?.id || null
        if (playerId) break
      }

      if (!playerId) {
        this.subscriptionState.error = 'No subscription ID from SDK'
        this.notifyListeners()
        return false
      }

      await this.savePlayerIdToDatabase(playerId)
      this.subscriptionState.playerId = playerId
      this.subscriptionState.isSubscribed = true
      this.subscriptionState.error = null
      this.notifyListeners()
      return true
    } catch (error: any) {
      console.error('[OneSignalService] Error subscribing:', error)
      this.subscriptionState.error = error?.message || 'Subscription failed'
      this.subscriptionState.isSubscribed = false
      this.notifyListeners()
      return false
    } finally {
      this.isSubscribing = false
    }
  }

  public async unsubscribe(): Promise<boolean> {
    if (!this.subscriptionState.isSupported) return false

    try {
      if (window.OneSignal?.User?.PushSubscription?.optOut) {
        await window.OneSignal.User.PushSubscription.optOut()
      }

      const playerId = this.subscriptionState.playerId
      if (playerId && this.currentUserId) {
        const { error } = await supabase
          .from('onesignal_players')
          .delete()
          .eq('user_id', this.currentUserId)
          .eq('player_id', playerId)

        if (error) console.error('[OneSignalService] Error removing player ID:', error)
      }

      this.subscriptionState.isSubscribed = false
      this.subscriptionState.playerId = null
      this.subscriptionState.error = null
      this.notifyListeners()
      return true
    } catch (error: any) {
      console.error('[OneSignalService] Error unsubscribing:', error)
      this.subscriptionState.error = error?.message || 'Unsubscribe failed'
      this.notifyListeners()
      return false
    }
  }

  public getSubscriptionState(): SubscriptionState {
    return { ...this.subscriptionState }
  }

  public onSubscriptionChange(callback: (state: SubscriptionState) => void): () => void {
    this.listeners.add(callback)
    callback(this.getSubscriptionState())
    return () => { this.listeners.delete(callback) }
  }

  private notifyListeners(): void {
    const state = this.getSubscriptionState()
    this.listeners.forEach(listener => {
      try {
        listener(state)
      } catch (error) {
        console.error('[OneSignalService] Error in listener:', error)
      }
    })
  }

  public async requestPermission(_strategy: PermissionStrategy): Promise<boolean> {
    if (!this.isInitialized || !window.OneSignal) return false
    return this.subscribe()
  }

  // ── OneSignal tags for notification preferences ────────────────────────────────
  // Sets a tag on the current user for filtering notifications by category.
  // Tags: training_reminders, class_requests, direct_messages with values '1' or '0'

  public async setTag(key: string, value: '1' | '0'): Promise<void> {
    if (!this.isInitialized || !window.OneSignal?.User?.addTag) return

    try {
      await window.OneSignal.User.addTag(key, value)
    } catch (error) {
      console.error('[OneSignalService] Error setting tag:', error)
    }
  }
}

export default OneSignalService.getInstance()
export { OneSignalService }
